/**
 * Pure formatting utilities for Incident Management
 * No React dependencies - safe for extraction
 */

import { capitalizeFirstLetter, capitalizeWords } from "Utils/utils";
import { BODY_PART_GROUPS } from "../constants";
import { frontRegions, backRegions } from "../data/Coordinates";

// Map of body part id -> display name (same source as Injury form for consistency)
const BODY_PART_REGION_NAMES = Object.fromEntries(
  [...frontRegions, ...backRegions].map((r) => [r.id, r.name]),
);

/**
 * Normalize body part id for lookup (handles underscore from mobile: Left_shoulder -> left-shoulder)
 */
const normalizePartForLookup = (part) => {
  if (!part || typeof part !== "string") return part;
  const s = part.toLowerCase().replace(/_/g, "-");
  const m = s.match(/^(.+)-(left|right)$/);
  if (m) return `${m[2]}-${m[1]}`;
  return s;
};

/**
 * Get body part display name - uses same labels as Injury Detail form (Coordinates)
 */
const getBodyPartDisplayName = (part) => {
  if (!part) return "";
  const normalized = normalizePartForLookup(part);
  return (
    BODY_PART_REGION_NAMES[part] ||
    BODY_PART_REGION_NAMES[normalized] ||
    formatBodyPartLabel(part)
  );
};

/**
 * Get label from options array by value
 */
export const getOptionLabel = (options, value) => {
  if (!value) return "-";
  const option = options.find((o) => o.value === value);
  const label = option?.label || value;
  return typeof label === "string" ? capitalizeWords(label) : label;
};

/**
 * Get multiple labels from options array
 */
export const getOptionLabels = (options, values) => {
  if (!values) return "-";
  if (typeof values === "string") {
    const option = options.find((o) => o.value === values);
    const label = option?.label || values;
    return typeof label === "string" ? capitalizeWords(label) : label;
  }
  if (!Array.isArray(values) || values.length === 0) return "-";
  return values
    .map((v) => {
      const option = options.find((o) => o.value === v);
      const label = option?.label || v;
      return typeof label === "string" ? capitalizeWords(label) : label;
    })
    .join(", ");
};

/**
 * Format boolean/yes/no values for display
 */
export const formatYesNo = (value) => {
  if (value === true || value === "yes" || value === "Yes") return "Yes";
  if (value === false || value === "no" || value === "No") return "No";
  if (value === null || value === undefined) return "-";
  if (
    value === "na" ||
    value === "NA" ||
    value === "not_applicable" ||
    value === "Not Applicable"
  )
    return "N/A";
  return typeof value === "string" ? capitalizeFirstLetter(value) : value;
};

/**
 * Format body part label (handles hyphens and underscores for web/mobile consistency)
 */
export const formatBodyPartLabel = (part) => {
  if (!part) return "";
  return part
    .split(/[-_]/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

/**
 * Group body parts by anatomical region.
 * Uses same display names as Injury Detail form (Coordinates) for consistency.
 * Any part not in BODY_PART_GROUPS is included in an "Other" group so none are dropped.
 */
export const getGroupedBodyParts = (bodyParts, allBodyParts) => {
  if (!bodyParts || !Array.isArray(bodyParts)) return [];

  const matched = new Set();
  const grouped = [];

  Object.entries(BODY_PART_GROUPS).forEach(([key, group]) => {
    const matchingParts = bodyParts.filter(
      (part) =>
        group.parts.includes(part) ||
        group.parts.includes(normalizePartForLookup(part)),
    );
    matchingParts.forEach((p) => matched.add(p));
    if (matchingParts.length > 0) {
      grouped.push({
        groupLabel: group.label,
        parts: matchingParts.map((part) => getBodyPartDisplayName(part)),
      });
    }
  });

  const unmatched = bodyParts.filter((part) => !matched.has(part));
  if (unmatched.length > 0) {
    grouped.push({
      groupLabel: "Other",
      parts: unmatched.map((part) => getBodyPartDisplayName(part)),
    });
  }

  return grouped;
};
